import { assignInlineVars } from "@vanilla-extract/dynamic";
import { useFormContext } from "react-hook-form";

import { FormField } from "checkout-experience/components/forms/form-field";
import {
  checkoutFormFieldStyle,
  flex,
  marginBottom,
  marginTop,
  minWidth,
} from "checkout-experience/components/forms/form-section.css";
import { PaymentOption } from "checkout-experience/components/payment-method/payment-option-provider";
import { useFormFieldsLoading } from "checkout-experience/hooks/use-form-fields-loading";
import { useSecurityCodeFieldBySettings } from "checkout-experience/hooks/use-security-code-field-by-settings";
import { CheckoutSession } from "checkout-query-client/hooks/use-checkout-session";
import { usePaymentForm } from "checkout-query-client/hooks/use-form";
import {
  ChexFieldNames,
  ChexFieldValues,
} from "checkout-query-client/types/chex-form-fields";
import {
  FormFieldGrid,
  FormInputConfig,
} from "checkout-query-client/types/form-types";

import {
  GetMerchantSettingsResponse_MerchantSettings,
  PlainMessage,
} from "frontend-client";

export interface ActiveFormFieldWrapperProps {
  field: FormInputConfig<ChexFieldNames>;
  checkoutSession: CheckoutSession | undefined;
  editSavedPaymentMethod: boolean | undefined;
  hasSavedProfile: boolean | undefined;
  hideSetAsDefault: boolean;
  merchantSettings:
    | PlainMessage<GetMerchantSettingsResponse_MerchantSettings>
    | undefined;
  checkoutSessionToken: string | undefined;
  paymentOption: PaymentOption | undefined;
  breakpoint: string;
}

export const ActiveFormFieldWrapper = ({
  field,
  checkoutSession,
  editSavedPaymentMethod,
  hasSavedProfile,
  hideSetAsDefault,
  merchantSettings,
  checkoutSessionToken,
  paymentOption,
  breakpoint,
}: ActiveFormFieldWrapperProps) => {
  const { formId = "", paymentType } = paymentOption ?? {};
  const { data: formConfig } = usePaymentForm({
    checkoutSession,
    merchantSettings,
    paymentType,
    formId,
    checkoutSessionToken,
  });

  const { fieldIsLoading, fieldHasLoaded } = useFormFieldsLoading();

  const { watch } = useFormContext();
  const { doAddressesMatch, currentShippingAddressWithEdit } = (watch(formId) ??
    {}) as ChexFieldValues;
  const isSavedPaymentMethod = formId !== paymentType?.toString();

  const doAddressesMatchValue = !isSavedPaymentMethod
    ? (doAddressesMatch ??
        formConfig?.fields["doAddressesMatch"]?.initialValue) === "true"
    : false;
  const editAddressClicked = isSavedPaymentMethod
    ? (currentShippingAddressWithEdit ??
        formConfig?.fields["currentShippingAddressWithEdit"]?.initialValue) ===
      "true"
    : true;

  const securityCodeForPay = useSecurityCodeFieldBySettings({
    editSavedPaymentMethod,
    paymentOption,
    checkoutSession,
    merchantSettings,
  });

  if (!field) return null;
  // If addresses matches then we do not want to show the billing address fields
  // unless user clicked edit button
  if (
    (doAddressesMatchValue || !editAddressClicked) &&
    field.id.includes("billingAddress")
  ) {
    return null;
  }

  if (!doAddressesMatchValue && field.id === "currentShippingAddress") {
    return null;
  }

  if (editAddressClicked && field.id === "currentShippingAddressWithEdit") {
    return null;
  }

  /**
   * do not render any field if not edit mode
   * render security code even in non-edit mode
   */
  if (
    editSavedPaymentMethod === false &&
    (!securityCodeForPay || (securityCodeForPay && field.id !== "securityCode"))
  ) {
    return null;
  }

  // hide setAsDefault field if there is (no saved payment method) AND (not a return user)
  // OR if (return user) AND it is (already a default) payment method.
  if (
    ((!isSavedPaymentMethod && !hasSavedProfile) ||
      (isSavedPaymentMethod && hideSetAsDefault)) &&
    field.id === "setAsDefault"
  ) {
    return null;
  }

  return (
    <div
      key={field.id}
      className={checkoutFormFieldStyle}
      style={assignInlineVars({
        [minWidth]:
          field.grid?.[breakpoint as keyof FormFieldGrid]?.minWidth || "100%",
        [flex]: field.grid?.[breakpoint as keyof FormFieldGrid]?.flex || "auto",
        [marginTop]:
          field.grid?.[breakpoint as keyof FormFieldGrid]?.marginTop || "0",
        [marginBottom]:
          field.grid?.[breakpoint as keyof FormFieldGrid]?.marginBottom || "0",
      })}
    >
      <FormField
        {...field}
        fieldIsLoading={() => fieldIsLoading(field.id)}
        fieldHasLoaded={() => fieldHasLoaded(field.id)}
      />
    </div>
  );
};
